// Shared Web Audio plumbing: ONE AudioContext for sfx + music, created and
// resumed on the first user gesture (browser autoplay policy — nothing can
// sound before then anyway), unlock callbacks so Music can start its stems
// the moment audio is allowed, and the persisted [audio] settings blob
// (localStorage).

let ctx: AudioContext | null = null;
const unlockCbs: (() => void)[] = [];

export function getCtx(): AudioContext | null {
  return ctx;
}

/** Run fn once audio is unlocked (immediately if it already is). */
export function onUnlock(fn: () => void): void {
  if (ctx && ctx.state === 'running') fn();
  else unlockCbs.push(fn);
}

function unlock(): void {
  if (!ctx) ctx = new AudioContext();
  if (ctx.state !== 'running') {
    void ctx.resume().then(fire);
    return;
  }
  fire();
}

function fire(): void {
  if (!ctx || ctx.state !== 'running') return;
  while (unlockCbs.length) unlockCbs.shift()!();
}

/** Arm the gesture listeners (idempotent; call from each audio module init). */
let armed = false;
export function armUnlock(): void {
  if (armed) return;
  armed = true;
  const h = () => {
    unlock();
    if (ctx && ctx.state === 'running') {
      document.removeEventListener('pointerdown', h);
      document.removeEventListener('keydown', h);
    }
  };
  document.addEventListener('pointerdown', h);
  document.addEventListener('keydown', h);
}

// ---------- persisted [audio] settings ----------
const KEY = 'stoneheim.audio';

export interface AudioSettings {
  muted?: boolean;
  volume?: number;
  music_volume?: number;
  music_vibe?: string;
}

export function loadAudioSettings(): AudioSettings {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? '{}');
  } catch {
    return {};
  }
}

export function saveAudioSettings(patch: AudioSettings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...loadAudioSettings(), ...patch }));
  } catch {
    // non-fatal: settings hold for this session only
  }
}

// ---------- tiny seeded RNG (deterministic composition/work-walk noise) ----------
export type Rng = () => number; // uniform [0,1)

export function mulberry32(seed: number): Rng {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function strHash(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h;
}
